/**
 * PendingRequestCard — FE-248 / FE-249.
 *
 * Shared inline card used by GroupDetailScreen ("Pending requests" section)
 * and ItemDetailScreen ("Pending requests" card on the owner's view). Single
 * source of truth for: requester avatar + name, item name (or group name),
 * optional message, time-ago, priority chip, waitlist position, and the
 * Approve / Decline action pair.
 *
 * The same backing thunks (approveBorrowRequest / denyBorrowRequest) are
 * already used by `BorrowRequestsManager` (the standalone inbox), so this
 * component is a thin layout wrapper — no new redux action surfaces.
 *
 * Variant prop drives which secondary line is shown:
 *  - `variant="group"` — used inside GroupDetail; subtitle line is the
 *    requested item's name.
 *  - `variant="item"` — used inside ItemDetail; subtitle line is the
 *    "via <group_name>" context.
 *
 * Optimistic flow: tap [Approve] / [Decline] flips the card into an
 * inline `<ActivityIndicator>` spinner. On success the card unmounts
 * naturally because the slice removes the row from `receivedRequests`.
 * On failure the spinner clears and a toast surfaces the error.
 */

import React, { useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';

import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import {
  approveBorrowRequest,
  denyBorrowRequest,
  refreshAllRequests,
} from '../borrowRequestsSlice';
import { selectUser } from '../../auth/authSlice';
import { DenyNotesSheet } from './DenyNotesSheet';
import type { BorrowRequestDetails } from '../../../api/borrowTypes';

export interface PendingRequestCardProps {
  request: BorrowRequestDetails;
  /** Which surface is rendering us — drives the subtitle line. */
  variant: 'group' | 'item';
  /** Optional tap target on the card body (excluding the action buttons).
   *  Used by GroupDetail to deep-link to the item's ItemDetail. */
  onPressBody?: () => void;
}

export const PendingRequestCard: React.FC<PendingRequestCardProps> = ({
  request,
  variant,
  onPressBody,
}) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const [pendingAction, setPendingAction] =
    useState<'approve' | 'decline' | null>(null);
  // FE-210 — RECALL requests appear on the owner's pending strip when
  // they recalled an item from a borrower. In that case `viewer === requester`
  // (the owner sent the request to themselves on the recall path) and the
  // standard "wants to borrow" / Approve / Decline copy reads as nonsense.
  // We render a distinct "RECALL PENDING · YOU SENT THIS" header with a
  // single Cancel-recall CTA instead.
  const priorityUpper = (request.priority || 'NORMAL').toUpperCase();
  const isViewerRecaller =
    priorityUpper === 'RECALL' &&
    request.requester_user_id != null &&
    currentUser?.id != null &&
    request.requester_user_id === currentUser.id;
  // FE-227 v2 — Decline now opens a sheet that lets the owner add an
  // optional reason. The sheet handles the actual API call + toast;
  // we just track its visibility here. Approve still fires inline
  // (no notes input on the happy path).
  const [denySheetVisible, setDenySheetVisible] = useState(false);

  // ── Derived display copy ───────────────────────────────────────────
  const requesterName = (request.requester_name ?? 'Someone').trim() || 'Someone';
  // First two letters for the avatar fallback. Splits on first space to
  // get initials when available ("Kanishk Patel" → "KP"); falls back to
  // first two characters otherwise.
  const initials = (() => {
    const parts = requesterName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return requesterName.slice(0, 2).toUpperCase();
  })();
  const message = (request.message ?? '').trim();
  const subtitle =
    variant === 'group'
      ? (request.item_name || 'an item')
      : request.group_name
        ? `via ${request.group_name}`
        : 'marketplace request';
  const timeAgo = formatTimeAgo(request.created_at);
  const priority =
    (request.priority || 'NORMAL').toUpperCase() as 'NORMAL' | 'URGENT' | 'RECALL';
  const queuePosition = request.queue_position ?? null;

  // ── Action handlers ────────────────────────────────────────────────
  // Approve fires inline (no input needed). Decline opens the
  // DenyNotesSheet which collects an optional reason then submits
  // via its own internal denyBorrowRequest dispatch.
  const handle = async (kind: 'approve' | 'decline') => {
    if (pendingAction != null) return; // already in-flight
    if (kind === 'decline') {
      setDenySheetVisible(true);
      return;
    }
    // Approve path — inline submit, no notes required.
    setPendingAction('approve');
    try {
      await dispatch(
        approveBorrowRequest({ requestId: request.id }) as any,
      ).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Approved',
        text2: `${requesterName} has been notified.`,
      });
      // FE-248 v2 hotfix — explicitly clear the spinner on success.
      // The slice flips status PENDING → APPROVED which makes the
      // parent's filter drop this row on next render. Clearing here
      // is harmless if unmount happens first.
      setPendingAction(null);
    } catch (err: any) {
      const message =
        typeof err === 'string'
          ? err
          : err?.message ?? 'Please try again.';
      const isStale =
        typeof message === 'string' &&
        /not found|cannot be modified|already|expired/i.test(message);
      Toast.show({
        type: 'error',
        text1: isStale ? 'Already actioned' : "Couldn't complete",
        text2: isStale
          ? "This request was already approved, declined, or expired. Refreshing the list."
          : message,
      });
      // Stale row in the inbox — pull a fresh fetch so the
      // already-actioned card disappears.
      if (isStale) {
        dispatch(refreshAllRequests() as any);
      }
      setPendingAction(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <View style={styles.card} testID={`pending-request-${request.id}`}>
      <Pressable
        onPress={onPressBody}
        disabled={!onPressBody || pendingAction != null}
        style={styles.body}
        accessibilityRole={onPressBody ? 'button' : 'text'}
        accessibilityLabel={`Borrow request from ${requesterName} for ${request.item_name ?? 'an item'}`}
      >
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{initials}</Text>
        </View>
        <View style={styles.bodyText}>
          {isViewerRecaller ? (
            <Text style={styles.requesterLine} numberOfLines={1}>
              <Text style={styles.requesterName}>Recall pending</Text>
              <Text style={styles.requesterVerb}> · you sent this</Text>
            </Text>
          ) : (
            <Text style={styles.requesterLine} numberOfLines={1}>
              <Text style={styles.requesterName}>{requesterName}</Text>
              <Text style={styles.requesterVerb}> wants to borrow</Text>
            </Text>
          )}
          <Text style={styles.subtitleLine} numberOfLines={1}>
            {isViewerRecaller
              ? `Awaiting acknowledgement on ${request.item_name ?? 'this item'}`
              : subtitle}
          </Text>
          {message ? (
            <Text style={styles.messageLine} numberOfLines={2}>
              {isViewerRecaller ? `Reason: "${message}"` : `"${message}"`}
            </Text>
          ) : null}
          <View style={styles.metaRow}>
            <Text style={styles.metaText}>{timeAgo}</Text>
            {priority !== 'NORMAL' ? (
              <View
                style={[
                  styles.chip,
                  priority === 'RECALL' ? styles.chipRecall : styles.chipUrgent,
                ]}
              >
                <Text
                  style={[
                    styles.chipText,
                    priority === 'RECALL'
                      ? styles.chipRecallText
                      : styles.chipUrgentText,
                  ]}
                >
                  {priority}
                </Text>
              </View>
            ) : null}
            {queuePosition != null && queuePosition > 1 ? (
              <View style={[styles.chip, styles.chipWaitlist]}>
                <Text style={[styles.chipText, styles.chipWaitlistText]}>
                  #{queuePosition} in waitlist
                </Text>
              </View>
            ) : null}
          </View>
        </View>
      </Pressable>

      {isViewerRecaller ? (
        // FE-210 — When the viewer is the owner who sent the recall, hide
        // Approve / Decline (which make no sense on a self-sent request).
        // The recall acknowledgement happens on the borrower's side; for
        // the owner, this card is purely informational until the borrower
        // confirms. A future Cancel-recall CTA can land here once the
        // backend endpoint is documented.
        <Text style={styles.recallInfoNote}>
          The borrower will be notified. You'll see this update when they
          acknowledge or return the item.
        </Text>
      ) : (
        <View style={styles.actions}>
          <Pressable
            onPress={() => handle('approve')}
            disabled={pendingAction != null}
            style={({ pressed }) => [
              styles.actionBtn,
              styles.actionBtnPrimary,
              (pressed || pendingAction === 'approve') && { opacity: 0.85 },
              pendingAction != null && pendingAction !== 'approve' && { opacity: 0.45 },
            ]}
            accessibilityRole="button"
            accessibilityLabel={`Approve ${requesterName}'s request`}
            testID={`pending-request-approve-${request.id}`}
          >
            {pendingAction === 'approve' ? (
              <ActivityIndicator size="small" color={fpColors.onPrimary} />
            ) : (
              <>
                <MaterialIcons name="check" size={14} color={fpColors.onPrimary} />
                <Text style={styles.actionBtnPrimaryText}>Approve</Text>
              </>
            )}
          </Pressable>
          <Pressable
            onPress={() => handle('decline')}
            disabled={pendingAction != null}
            style={({ pressed }) => [
              styles.actionBtn,
              styles.actionBtnSecondary,
              (pressed || pendingAction === 'decline') && { opacity: 0.85 },
              pendingAction != null && pendingAction !== 'decline' && { opacity: 0.45 },
            ]}
            accessibilityRole="button"
            accessibilityLabel={`Decline ${requesterName}'s request`}
            testID={`pending-request-decline-${request.id}`}
          >
            {pendingAction === 'decline' ? (
              <ActivityIndicator size="small" color={fpColors.primary} />
            ) : (
              <Text style={styles.actionBtnSecondaryText}>Decline</Text>
            )}
          </Pressable>
        </View>
      )}

      {/* FE-227 v2 — DenyNotesSheet mounted as a sibling of the card
          body so the Modal's portal logic doesn't interfere with the
          card's layout. Mounted conditionally (rather than always
          present) to keep the React tree tiny on the 99% of cards
          where the sheet isn't open. */}
      {denySheetVisible ? (
        <DenyNotesSheet
          visible
          request={request}
          onClose={() => setDenySheetVisible(false)}
          /* No onSubmitted handler needed — the slice flips the row's
             status to DENIED on success, the parent's filter
             (PENDING/WAITLISTED only) drops the card, and this
             component unmounts naturally. */
        />
      ) : null}
    </View>
  );
};

// ── helpers ──────────────────────────────────────────────────────────

/** Tiny self-contained time-ago renderer. Stays local so PendingRequestCard
 *  doesn't pull in a date-fns dependency just for one line of copy.
 *  Renders: "just now", "5m ago", "3h ago", "2d ago", "Apr 28". */
function formatTimeAgo(iso: string | undefined | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

// ── styles ───────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    backgroundColor: fpColors.surface,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    padding: 14,
    marginBottom: 10,
    gap: 12,
  },
  body: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: (fpColors as any).primaryContainer ?? '#EADDFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: (fpColors as any).onPrimaryContainer ?? fpColors.primary,
  },
  bodyText: {
    flex: 1,
    gap: 3,
  },
  requesterLine: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  requesterName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  requesterVerb: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  subtitleLine: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
  messageLine: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    fontStyle: 'italic',
    marginTop: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    flexWrap: 'wrap',
  },
  metaText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
  },
  chipText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.4,
  },
  chipUrgent: {
    backgroundColor:
      (fpColors as any).tertiaryContainer ??
      (fpColors as any).primaryContainer ??
      '#FFE0B2',
  },
  chipUrgentText: {
    color:
      (fpColors as any).onTertiaryContainer ??
      (fpColors as any).onPrimaryContainer ??
      fpColors.primary,
  },
  chipRecall: {
    backgroundColor: (fpColors as any).errorContainer ?? '#FFDAD6',
  },
  chipRecallText: {
    color: (fpColors as any).onErrorContainer ?? fpColors.error,
  },
  chipWaitlist: {
    backgroundColor: fpColors.surfaceVariant ?? '#E7E0EC',
  },
  chipWaitlistText: {
    color: fpColors.onSurfaceVariant,
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 9,
    borderRadius: 999,
    minHeight: 36,
  },
  actionBtnPrimary: {
    backgroundColor: fpColors.primary,
  },
  actionBtnPrimaryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onPrimary,
    letterSpacing: 0.2,
  },
  actionBtnSecondary: {
    backgroundColor: fpColors.surface,
    borderWidth: 1,
    borderColor: fpColors.outline,
  },
  actionBtnSecondaryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.primary,
    letterSpacing: 0.2,
  },
  // FE-210 — Informational caption shown in place of Approve/Decline
  // when the viewer is the recall sender (owner). Same surface style
  // as `messageLine` for visual consistency.
  recallInfoNote: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    fontStyle: 'italic',
    paddingTop: 2,
  },
});
